import * as Sentry from '@sentry/gatsby'
import fetch from 'cross-fetch'

import { BasketReturnType, QueueRequest } from './BasketTypes'

interface ReportBasketErrorType {
  type: 'Message' | 'NoJSON'
  status: number
  error: string
  body?: string
  requestBody?: object
}

const reportBasketError = ({ type, error, body, requestBody }: ReportBasketErrorType) =>
  Sentry.captureMessage(`Basket API: ${type} error`, {
    tags: { component: `BasketAPI${type}` },
    extra: {
      errorMessage: error,
      body,
      requestBody,
    },
  })

const getUrlEndpoint = (endpoint: string): RequestInfo => {
  const version = process.env.BASKET_API_VERSION

  return `/api/${version}/basket${endpoint}`
}

export function callBasketApi(request: QueueRequest) {
  const headersObj: Record<string, string> = {}

  if (!request.formData) {
    headersObj['Content-Type'] = 'application/json'
  }

  return new Promise<BasketReturnType>((resolve) => {
    const caller = async () => {
      try {
        const response = await fetch(getUrlEndpoint(request.endpoint), {
          method: request.method,
          credentials: 'include',
          mode: 'cors',
          headers: headersObj,
          body: request.formData ? request.formData : JSON.stringify(request.body),
        })

        if (response.ok) {
          const responseText = await response.text()

          if (responseText.startsWith('{')) {
            return resolve([null, JSON.parse(responseText)])
          }

          return resolve([null, responseText])
        }

        // Handle errors
        try {
          const error = await response.json()

          reportBasketError({
            type: 'Message',
            status: response.status,
            error,
            requestBody: request.body,
          })

          return resolve([
            { message: error?.message ?? response.statusText, status: response.status, error },
            null,
          ])
        } catch (err: unknown) {
          const basketError: ReportBasketErrorType = {
            type: 'NoJSON',
            status: response.status,
            error: `${err}`,
            requestBody: request.body,
          }

          response
            .text()
            .then((errorText) => reportBasketError({ ...basketError, body: errorText }))
            .catch(() => reportBasketError(basketError))
        }
      } catch (_error: unknown) {
        // Ignore
      }

      resolve([{ message: 'error' }, null])
    }

    void caller()
  })
}
